import type { PlayerAttributes, Role } from '../types.js';

type AttributeKey = keyof PlayerAttributes;
type WeightMap = Record<AttributeKey, number>;

const DEFAULT_ATTRIBUTE_FALLBACK: Record<AttributeKey, number> = {
    pace: 50,
    ballControl: 50,
    passing: 50,
    tackling: 50,
    endurance: 50,
    morale: 60,
    positioning: 50,
    shotPower: 50,
    jumping: 50,
    reactions: 50,
    composure: 50,
    goalkeeping: 30
};

const ROLE_WEIGHT_MAP: Record<string, WeightMap> = {
    GK: {
        pace: 0.01, ballControl: 0.01, passing: 0.04, tackling: 0, endurance: 0.02, morale: 0.02,
        positioning: 0.12, shotPower: 0, jumping: 0.06, reactions: 0.16, composure: 0.08, goalkeeping: 0.48
    },
    CB: {
        pace: 0.06, ballControl: 0.03, passing: 0.08, tackling: 0.24, endurance: 0.06, morale: 0.02,
        positioning: 0.2, shotPower: 0.01, jumping: 0.14, reactions: 0.1, composure: 0.06, goalkeeping: 0
    },
    LB: {
        pace: 0.18, ballControl: 0.08, passing: 0.12, tackling: 0.18, endurance: 0.14, morale: 0.02,
        positioning: 0.14, shotPower: 0.02, jumping: 0.03, reactions: 0.08, composure: 0.01, goalkeeping: 0
    },
    RB: {
        pace: 0.18, ballControl: 0.08, passing: 0.12, tackling: 0.18, endurance: 0.14, morale: 0.02,
        positioning: 0.14, shotPower: 0.02, jumping: 0.03, reactions: 0.08, composure: 0.01, goalkeeping: 0
    },
    CDM: {
        pace: 0.05, ballControl: 0.07, passing: 0.18, tackling: 0.2, endurance: 0.14, morale: 0.02,
        positioning: 0.16, shotPower: 0.01, jumping: 0.01, reactions: 0.08, composure: 0.08, goalkeeping: 0
    },
    CM: {
        pace: 0.05, ballControl: 0.16, passing: 0.22, tackling: 0.07, endurance: 0.14, morale: 0.02,
        positioning: 0.14, shotPower: 0.02, jumping: 0.01, reactions: 0.08, composure: 0.09, goalkeeping: 0
    },
    CAM: {
        pace: 0.07, ballControl: 0.2, passing: 0.2, tackling: 0.03, endurance: 0.05, morale: 0.02,
        positioning: 0.1, shotPower: 0.12, jumping: 0.01, reactions: 0.08, composure: 0.12, goalkeeping: 0
    },
    LM: {
        pace: 0.21, ballControl: 0.18, passing: 0.14, tackling: 0.02, endurance: 0.1, morale: 0.02,
        positioning: 0.06, shotPower: 0.13, jumping: 0.01, reactions: 0.08, composure: 0.05, goalkeeping: 0
    },
    RM: {
        pace: 0.21, ballControl: 0.18, passing: 0.14, tackling: 0.02, endurance: 0.1, morale: 0.02,
        positioning: 0.06, shotPower: 0.13, jumping: 0.01, reactions: 0.08, composure: 0.05, goalkeeping: 0
    },
    LW: {
        pace: 0.21, ballControl: 0.18, passing: 0.14, tackling: 0.02, endurance: 0.1, morale: 0.02,
        positioning: 0.06, shotPower: 0.13, jumping: 0.01, reactions: 0.08, composure: 0.05, goalkeeping: 0
    },
    RW: {
        pace: 0.21, ballControl: 0.18, passing: 0.14, tackling: 0.02, endurance: 0.1, morale: 0.02,
        positioning: 0.06, shotPower: 0.13, jumping: 0.01, reactions: 0.08, composure: 0.05, goalkeeping: 0
    },
    ST: {
        pace: 0.1, ballControl: 0.05, passing: 0.03, tackling: 0.01, endurance: 0.02, morale: 0.02,
        positioning: 0.2, shotPower: 0.25, jumping: 0.07, reactions: 0.12, composure: 0.13, goalkeeping: 0
    },
    CF: {
        pace: 0.1, ballControl: 0.09, passing: 0.08, tackling: 0.01, endurance: 0.03, morale: 0.02,
        positioning: 0.16, shotPower: 0.2, jumping: 0.05, reactions: 0.11, composure: 0.15, goalkeeping: 0
    }
};

const DEFAULT_OUTFIELD_WEIGHTS: WeightMap = {
    pace: 0.11, ballControl: 0.13, passing: 0.13, tackling: 0.1, endurance: 0.1, morale: 0.02,
    positioning: 0.12, shotPower: 0.1, jumping: 0.05, reactions: 0.08, composure: 0.06, goalkeeping: 0
};

function clamp(min: number, max: number, value: number): number {
    return Math.max(min, Math.min(max, value));
}

function normalizeRole(role: string): string {
    const key = String(role ?? '').toUpperCase();
    return ROLE_WEIGHT_MAP[key] ? key : 'OUTFIELD';
}

function toStat(attributes: Partial<PlayerAttributes> | undefined, key: AttributeKey): number {
    const numeric = Number(attributes?.[key]);
    if (!Number.isFinite(numeric)) {
        return DEFAULT_ATTRIBUTE_FALLBACK[key];
    }
    return clamp(1, 99, numeric);
}

export function getRoleAttributeWeights(role: Role | string): WeightMap {
    const key = normalizeRole(role);
    return key === 'OUTFIELD' ? DEFAULT_OUTFIELD_WEIGHTS : ROLE_WEIGHT_MAP[key];
}

export function computeRoleOverallRating(role: Role | string, attributes: Partial<PlayerAttributes> | undefined): number {
    const weights = getRoleAttributeWeights(role);
    const score = (Object.keys(weights) as AttributeKey[]).reduce((total, key) => (
        total + (weights[key] * toStat(attributes, key))
    ), 0);
    return Math.round(clamp(1, 99, score));
}

export function rankRoleRelevantAttributes(role: Role | string, attributes: Partial<PlayerAttributes> | undefined): Array<{
    key: AttributeKey;
    value: number;
    weight: number;
}> {
    const weights = getRoleAttributeWeights(role);
    return (Object.keys(weights) as AttributeKey[])
        .filter((key) => weights[key] >= 0.07)
        .map((key) => ({ key, value: toStat(attributes, key), weight: weights[key] }))
        .sort((a, b) => b.value - a.value);
}
